import { DeepPartial } from 'react-hook-form';
import {
    ActivityNimReadDTO,
    ActivityTypeCode,
    DurationTypeCode,
    FacilityCode,
    ListOfValuesReadDTO,
    PersonRichReadDTO,
    WorkingTimeCode,
} from '../../../api/dto';

export const makeDefaultValuesFromClonedActivity = (originalActivity: ActivityNimReadDTO, isTemplate: boolean) => {
    const { id, version, espQuestions, activityLocations, testQuestions, proposedInterventionPeriod, ...rest } =
        originalActivity;

    const surfaceActivitiesDefaults = {
        priority: null,
        system: null,
        maxNbParticipants: null,
        materialResource: null,
        materialResourceList: [],
        constraints: null,
        waste: null,
    } satisfies Pick<
        ActivityNimReadDTO,
        | 'priority'
        | 'system'
        | 'maxNbParticipants'
        | 'materialResource'
        | 'materialResourceList'
        | 'constraints'
        | 'waste'
    >;

    const LHCActivitiesDefaults = {
        earliestStart: null,
        latestEnd: null,
        warningDuration: null,
        warningDurationType: null,
        materialResource: null,
        materialResourceList: [],
        constraints: null,
        waste: null,
    } satisfies Pick<
        ActivityNimReadDTO,
        | 'earliestStart'
        | 'latestEnd'
        | 'warningDuration'
        | 'warningDurationType'
        | 'materialResource'
        | 'materialResourceList'
        | 'constraints'
        | 'waste'
    >;

    const clonedActivityLocations = activityLocations.map((activityLoc) => ({ ...activityLoc, accessPoints: [] }));
    const clonedTestQuestions = testQuestions.map((testQuestion) =>
        testQuestion.answer === null ? { ...testQuestion, answer: false } : testQuestion
    );

    return {
        ...rest,
        ...(originalActivity.facility?.code === FacilityCode.SURFACE ? surfaceActivitiesDefaults : {}),
        ...(originalActivity.facility?.code === FacilityCode.LHC ? LHCActivitiesDefaults : {}),
        activityLocations: clonedActivityLocations,
        proposedInterventionPeriod:
            proposedInterventionPeriod && Date.parse(proposedInterventionPeriod.periodEnd) < Date.now()
                ? proposedInterventionPeriod
                : null,
        legacyActivityChangeRequests: [],
        contaminatingWorks: false,
        totalCollectiveWorkingTime: null,
        maxIndividualWorkingTime: null,
        createdBy: null as any,
        proposedDate: null as any,
        createdDate: null as any,
        firePermits: [],
        is37s: [],
        notesDeCoupure: [],
        vics: [],
        lockouts: [],
        historicalRpAssessments: [],
        rpAssessment: null,
        scheduledStart: null,
        scheduledEnd: null,
        accessStart: null,
        accessEnd: null,
        interventionPeriod: null,
        workingTimeGrtdStart: null,
        workingTimeGrtdEnd: null,
        workingTimeGtd: null,
        workingTimeGrtdTxt: null,
        whenComments: null,
        whoComments: null,
        whereComments: null,
        triggeredAlarms: [],
        testQuestions: clonedTestQuestions,
        espQuestions: espQuestions
            ? {
                  ...espQuestions,
                  modusOperandiDocumentId: null,
              }
            : {
                  id: null,
                  requestForElectricalRiskAssessment: null,
                  modusOperandiDetailLevel: null,
                  modusOperandiDocumentId: null,
                  otherHazards: null,
                  preassessmentConfirmation: null,
                  equipmentWorkedOn: [],
                  equipmentWorkedOnText: null,
                  lockoutRequestStartDate: null,
                  lockoutRequestEndDate: null,
              },
        isTemplate,
        radiographyAffectedZoneId: null,
    } satisfies Omit<ActivityNimReadDTO, 'id' | 'version'>;
};

const WORK_DAY_START_HOUR = 8;
const WORK_DAY_START_MINUTE = 30;
const WORK_DAY_END_HOUR = 17;
const WORK_DAY_END_MINUTE = 30;

export const makeDefaultActivityValues = (
    activityTypes: ListOfValuesReadDTO[],
    testQuestionTypes: ListOfValuesReadDTO[],
    workingTimes: ListOfValuesReadDTO[],
    durationTypes: ListOfValuesReadDTO[],
    isUrgentIntervention: boolean,
    loggedUser: PersonRichReadDTO
) => {
    const maintenanceType = activityTypes.find(({ code }) => code === ActivityTypeCode.MAINTENANCE);
    const normalWorkingTime = workingTimes.find(({ code }) => code === WorkingTimeCode.NORMAL);
    const dayDurationType = durationTypes.find(({ code }) => code === DurationTypeCode.DAY);
    const urgentInterventionType = activityTypes.find(({ code }) => code === ActivityTypeCode.URGENT_INTERVENTION);
    const startDateTime = new Date();
    startDateTime.setHours(WORK_DAY_START_HOUR, WORK_DAY_START_MINUTE);
    const endDateTime = new Date();
    endDateTime.setHours(WORK_DAY_END_HOUR, WORK_DAY_END_MINUTE);
    return {
        activityResponsible: isUrgentIntervention ? loggedUser : null,
        activityLocations: [
            {
                accessPoints: [],
            },
        ],
        type: isUrgentIntervention ? urgentInterventionType : maintenanceType,
        workingTimeAvail: normalWorkingTime,
        workingTimeGtd: normalWorkingTime, // Hidden for most users
        durationType: dayDurationType,
        contaminatingWorks: false,
        shortTermWorkers: false,
        testQuestions: testQuestionTypes.map((r) => {
            return {
                type: r,
                comments: null,
                answer: false,
            };
        }),
        espQuestions: {
            requestForElectricalRiskAssessment: null,
        },
        workingTimeAvailStart: startDateTime.toISOString(),
        workingTimeAvailEnd: endDateTime.toISOString(),
        workingTimeGrtdStart: startDateTime.toISOString(),
        workingTimeGrtdEnd: endDateTime.toISOString(),
    } satisfies DeepPartial<ActivityNimReadDTO>;
};

export const facilitiesWithExtWorkingTime: string[] = [
    FacilityCode.LHC,
    FacilityCode.HL_LHC,
    FacilityCode.BOOSTER,
    FacilityCode.PS,
    FacilityCode.SPS,
    FacilityCode.LHCB,
];

export const facilitiesWithOtherWorkingTime: string[] = [
    FacilityCode.HL_LHC_IT_STRING,
    FacilityCode.ISOLDE,
    FacilityCode.MEDICIS,
    FacilityCode.HIRADMAT,
    FacilityCode.EAST_TARGET,
    FacilityCode.NORTH_AREA,
    FacilityCode.AWAKE,
    FacilityCode.NTOF,
    FacilityCode.CIXP,
    FacilityCode.CMS,
];

export const facilitiesWithAnyWorkingTime: string[] = [
    FacilityCode.AD_HALL_EXPERIMENTS,
    FacilityCode.ALICE,
    FacilityCode.ATLAS,
    FacilityCode.NA62,
    FacilityCode.NEUTRINO_PLATFORM,
];

const lhcOrHlLhcFacility: string[] = [FacilityCode.LHC, FacilityCode.HL_LHC, FacilityCode.HL_LHC_IT_STRING];

export const getAccessStartByScheduledStart = (scheduledStartDate: string, facilityValue: string | undefined) => {
    if (!facilityValue) return scheduledStartDate;
    if (lhcOrHlLhcFacility.includes(facilityValue)) {
        const date = new Date(scheduledStartDate);
        date.setDate(date.getDate() - 1);
        return date.toISOString();
    }
    return scheduledStartDate;
};

export const getAccessEndByScheduledEnd = (
    scheduledEndDate: string,
    facilityValue: string | undefined,
    periodEnd: string | undefined
) => {
    if (!facilityValue) return scheduledEndDate;
    if (lhcOrHlLhcFacility.includes(facilityValue)) {
        const date = new Date(scheduledEndDate);
        date.setDate(date.getDate() + 1);
        const nextDay = date.toISOString();
        if (periodEnd && Date.parse(periodEnd) >= Date.parse(nextDay)) return nextDay;
        return scheduledEndDate;
    }
    return scheduledEndDate;
};
